import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertTriangle,
  IconCheck,
  IconEdit,
  IconPlus,
  IconShieldCheck,
  IconTrash,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomDatePicker } from '../../components/custom-date-picker';
import { EpiTutorial } from '../../components/epi-tutorial';
import { Page } from '../../components/page';
import { EpiRoundItemInput } from '../../data/epi-rounds';
import { useEpiCatalog } from '../../data/hooks/employee';
import {
  useCreateEpiRound,
  useDeleteEpiRound,
  useListEpiRounds,
  useListPendingEpiRoundItems,
  useShowEpiRound,
  useUpdateEpiRound,
} from '../../data/hooks/epi-rounds';
import { useGetUser } from '../../data/hooks/user';
import { formatLocale } from '../../providers/dayjs-plugins';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';

const PAGE_SIZE = 15;

const SITUATION_OPTIONS = [
  { value: 'ok', label: 'OK' },
  { value: 'desgastado', label: 'Desgastado' },
  { value: 'danificado', label: 'Danificado' },
  { value: 'sem_epi', label: 'Funcionário sem EPI' },
  { value: 'necessita_reposicao', label: 'Necessita reposição' },
  { value: 'uso_inadequado', label: 'Uso inadequado' },
];

const SITUATION_LABEL: Record<string, string> = Object.fromEntries(
  SITUATION_OPTIONS.map((o) => [o.value, o.label]),
);

const SITUATION_COLORS: Record<string, string> = {
  ok: 'green',
  desgastado: 'yellow',
  danificado: 'red',
  sem_epi: 'red',
  necessita_reposicao: 'orange',
  uso_inadequado: 'orange',
};

const SITUATION_ACTION: Record<string, string> = {
  desgastado: 'Substituir',
  danificado: 'Substituir',
  sem_epi: 'Entregar EPI',
  necessita_reposicao: 'Repor',
  uso_inadequado: 'Orientar',
};

function SituationBadge({ situation }: { situation: string }) {
  return (
    <Badge color={SITUATION_COLORS[situation] ?? 'gray'} size="sm">
      {SITUATION_LABEL[situation] ?? situation}
    </Badge>
  );
}

type ItemWithKey = EpiRoundItemInput & { _key: string };

let _keyCounter = 0;
function nextKey() {
  return String(++_keyCounter);
}

export function EpiRounds() {
  const [page, setPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [items, setItems] = useState<ItemWithKey[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const {
    fetch: listRoundsFetcher,
    rounds,
    loading: listLoading,
  } = useListEpiRounds();
  const { create: createRound, loading: createLoading } = useCreateEpiRound();
  const { update: updateRound, loading: updateLoading } = useUpdateEpiRound();
  const {
    fetch: showRound,
    round: roundDetail,
    loading: showLoading,
  } = useShowEpiRound();
  const { remove: deleteRound } = useDeleteEpiRound();
  const {
    fetch: listPendingFetcher,
    items: pendingItems,
    loading: pendingLoading,
  } = useListPendingEpiRoundItems();
  const { fetch: listUsersFetcher, response: users } = useGetUser();
  const { list: listCatalogFetcher, catalog } = useEpiCatalog();

  const form = useForm({
    initialValues: {
      date: null as Date | null,
      unit: '',
      sector: '' as string | null,
      inspector: '',
      notes: '',
    },
  });

  const itemForm = useForm({
    initialValues: {
      employeeId: '' as string | null,
      epiId: '' as string | null,
      situation: '' as string | null,
      observation: '',
    },
  });

  const fetchPage = useCallback(
    (p: number) => listRoundsFetcher({ page: p, limit: PAGE_SIZE }),
    [],
  );

  useEffect(() => {
    fetchPage(1);
    listPendingFetcher();
    listUsersFetcher();
    listCatalogFetcher();
  }, []);

  useEffect(() => {
    if (!roundDetail || editingId === null) return;
    form.setValues({
      date: new Date(roundDetail.date),
      unit: roundDetail.unit ?? '',
      sector: roundDetail.sector ?? null,
      inspector: roundDetail.inspector ?? '',
      notes: roundDetail.notes ?? '',
    });
    setItems(
      (roundDetail.items ?? []).map((item: any) => ({
        _key: nextKey(),
        employeeId: item.employeeId ?? undefined,
        epiId: item.epiId ?? undefined,
        situation: item.situation,
        observation: item.observation ?? undefined,
      })),
    );
  }, [roundDetail, editingId]);

  // Memoized lookup maps — rebuilt only when source data changes
  const employeeOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((u: any) => u.employee?.id)
        .map((u: any) => ({ value: String(u.employee.id), label: u.username })),
    [users],
  );

  const employeeLabel = useMemo(
    () => Object.fromEntries(employeeOptions.map((o) => [o.value, o.label])),
    [employeeOptions],
  );

  const epiOptions = useMemo(
    () =>
      (Array.isArray(catalog) ? catalog : []).map((e: any) => ({
        value: String(e.id),
        label: e.name,
      })),
    [catalog],
  );

  const epiLabel = useMemo(
    () => Object.fromEntries(epiOptions.map((o) => [o.value, o.label])),
    [epiOptions],
  );

  function addItem() {
    if (!itemForm.values.situation) return;
    setItems((prev) => [
      ...prev,
      {
        _key: nextKey(),
        employeeId: itemForm.values.employeeId
          ? Number(itemForm.values.employeeId)
          : undefined,
        epiId: itemForm.values.epiId
          ? Number(itemForm.values.epiId)
          : undefined,
        situation: itemForm.values.situation ?? '',
        observation: itemForm.values.observation || undefined,
      },
    ]);
    itemForm.reset();
  }

  function removeItem(key: string) {
    setItems((prev) => prev.filter((i) => i._key !== key));
  }

  function handleCreate(values: typeof form.values) {
    if (!values.date) return;
    createRound(
      {
        date: values.date.toISOString(),
        unit: values.unit || undefined,
        sector: values.sector || undefined,
        inspector: values.inspector || undefined,
        notes: values.notes || undefined,
        items,
      },
      () => {
        setCreateModalOpen(false);
        form.reset();
        setItems([]);
        fetchPage(1);
        setPage(1);
        listPendingFetcher();
      },
    );
  }

  function handleEdit(r: any) {
    showRound(r.id).then(() => {
      setEditingId(r.id);
    });
  }

  function handleUpdate(values: typeof form.values) {
    if (!editingId || !values.date) return;
    updateRound(
      editingId,
      {
        date: values.date.toISOString(),
        unit: values.unit || undefined,
        sector: values.sector || undefined,
        inspector: values.inspector || undefined,
        notes: values.notes || undefined,
        items,
      },
      () => {
        setEditingId(null);
        form.reset();
        setItems([]);
        fetchPage(page);
        listPendingFetcher();
      },
    );
  }

  function handleDelete(id: number) {
    setDeletingId(id);
    deleteRound(id, () => {
      setDeletingId(null);
      fetchPage(page);
      listPendingFetcher();
    });
  }

  function handlePageChange(p: number) {
    setPage(p);
    fetchPage(p);
  }

  function closeModal() {
    setCreateModalOpen(false);
    setEditingId(null);
    form.reset();
    itemForm.reset();
    setItems([]);
  }

  const tableRounds = rounds?.items ?? [];
  const totalRounds = rounds?.total ?? 0;
  const tablePending = pendingItems ?? [];

  return (
    <Page>
      <Flex justify="space-between" align="center" mb={16}>
        <Title order={3}>Rondas de EPI</Title>
        <Group spacing={8}>
          <EpiTutorial screen="rounds" />
          <Button
            leftIcon={<IconPlus size={16} />}
            onClick={() => setCreateModalOpen(true)}
          >
            Nova Ronda
          </Button>
        </Group>
      </Flex>

      <Tabs defaultValue="rondas">
        <Tabs.List mb={16}>
          <Tabs.Tab value="rondas" icon={<IconShieldCheck size={14} />}>
            Rondas
          </Tabs.Tab>
          <Tabs.Tab
            value="pendencias"
            icon={<IconAlertTriangle size={14} />}
            color={tablePending.length > 0 ? 'red' : undefined}
          >
            Pendências
            {tablePending.length > 0 && (
              <Badge color="red" size="xs" variant="filled" ml={6}>
                {tablePending.length}
              </Badge>
            )}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="rondas">
          <DataTable
            minHeight={200}
            withBorder
            borderRadius="sm"
            striped
            highlightOnHover
            noRecordsText="Nenhuma ronda encontrada"
            fetching={listLoading}
            records={tableRounds}
            totalRecords={totalRounds}
            recordsPerPage={PAGE_SIZE}
            page={page}
            onPageChange={handlePageChange}
            columns={[
              {
                accessor: 'date',
                title: 'Data',
                render: (r: any) => formatLocale(r.date, 'DD/MM/YYYY HH:mm'),
              },
              { accessor: 'unit', title: 'Unidade' },
              { accessor: 'sector', title: 'Setor' },
              { accessor: 'inspector', title: 'Responsável' },
              {
                accessor: 'itemCount',
                title: 'Itens',
              },
              {
                accessor: 'issueCount',
                title: 'Problemas',
                render: (r: any) =>
                  r.issueCount > 0 ? (
                    <Badge color="red" size="sm">
                      {r.issueCount}
                    </Badge>
                  ) : (
                    <Badge
                      color="green"
                      size="sm"
                      leftSection={<IconCheck size={10} />}
                    >
                      OK
                    </Badge>
                  ),
              },
              {
                accessor: 'actions',
                title: '',
                render: (r: any) => (
                  <Group spacing={4} noWrap>
                    <ActionIcon
                      variant="subtle"
                      loading={showLoading && editingId === r.id}
                      onClick={() => handleEdit(r)}
                    >
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      loading={deletingId === r.id}
                      onClick={() => handleDelete(r.id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ),
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="pendencias">
          <DataTable
            minHeight={200}
            withBorder
            borderRadius="sm"
            striped
            highlightOnHover
            noRecordsText="Nenhuma pendência encontrada"
            fetching={pendingLoading}
            records={tablePending}
            columns={[
              {
                accessor: 'roundDate',
                title: 'Data da Ronda',
                render: (r: any) => formatLocale(r.roundDate, 'DD/MM/YYYY'),
              },
              { accessor: 'employeeName', title: 'Funcionário' },
              { accessor: 'sector', title: 'Setor' },
              { accessor: 'epiName', title: 'EPI' },
              {
                accessor: 'situation',
                title: 'Situação',
                render: (r: any) => <SituationBadge situation={r.situation} />,
              },
              {
                accessor: 'action',
                title: 'Ação',
                render: (r: any) => (
                  <Text size="sm" color="dimmed">
                    {SITUATION_ACTION[r.situation] ?? '—'}
                  </Text>
                ),
              },
              {
                accessor: 'observation',
                title: 'Observação',
                render: (r: any) => (
                  <Text size="sm" color="dimmed">
                    {r.observation ?? '—'}
                  </Text>
                ),
              },
            ]}
          />
        </Tabs.Panel>
      </Tabs>

      <Modal
        title={
          <Text fw={600} size="lg">
            {editingId ? 'Editar Ronda de EPI' : 'Nova Ronda de EPI'}
          </Text>
        }
        opened={createModalOpen || editingId !== null}
        onClose={closeModal}
        size="xl"
      >
        <form onSubmit={form.onSubmit(editingId ? handleUpdate : handleCreate)}>
          <Text size="xs" tt="uppercase" fw={600} color="dimmed" mb={10}>
            Dados da ronda
          </Text>
          <Flex wrap="wrap" gap={12} mb={4}>
            <CustomDatePicker
              label="Data"
              placeholder="selecione a data"
              required
              style={{ flex: '1 1 160px' }}
              {...form.getInputProps('date')}
            />
            <TextInput
              label="Unidade"
              placeholder="ex: Sede, Filial SP"
              style={{ flex: '1 1 160px' }}
              {...form.getInputProps('unit')}
            />
            <Select
              label="Setor"
              placeholder="selecione"
              searchable
              clearable
              data={SECTOR_OPTIONS}
              style={{ flex: '1 1 160px' }}
              {...form.getInputProps('sector')}
            />
            <TextInput
              label="Responsável"
              placeholder="nome do responsável"
              style={{ flex: '1 1 160px' }}
              {...form.getInputProps('inspector')}
            />
          </Flex>
          <Textarea
            label="Observações gerais"
            placeholder="observações sobre a ronda"
            autosize
            minRows={2}
            mb={20}
            {...form.getInputProps('notes')}
          />

          <Divider mb={16} />

          <Text size="xs" tt="uppercase" fw={600} color="dimmed" mb={10}>
            Itens verificados
          </Text>

          <Box
            p={12}
            mb={12}
            style={{
              background: '#f8f9fa',
              borderRadius: 8,
              border: '1px solid #e9ecef',
            }}
          >
            <Flex wrap="wrap" gap={10} align="flex-end">
              <Select
                label="Funcionário"
                placeholder="selecione"
                searchable
                clearable
                data={employeeOptions}
                style={{ flex: '1 1 160px' }}
                {...itemForm.getInputProps('employeeId')}
              />
              <Select
                label="EPI verificado"
                placeholder="selecione"
                searchable
                clearable
                data={epiOptions}
                style={{ flex: '1 1 160px' }}
                {...itemForm.getInputProps('epiId')}
              />
              <Select
                label="Situação"
                placeholder="selecione"
                data={SITUATION_OPTIONS}
                style={{ flex: '1 1 160px' }}
                {...itemForm.getInputProps('situation')}
              />
              <TextInput
                label="Observação"
                placeholder="observação opcional"
                style={{ flex: '1 1 160px' }}
                {...itemForm.getInputProps('observation')}
              />
              <Button
                leftIcon={<IconPlus size={14} />}
                onClick={addItem}
                disabled={!itemForm.values.situation}
                mb={1}
              >
                Adicionar
              </Button>
            </Flex>
          </Box>

          <ScrollArea
            h={items.length === 0 ? 48 : Math.min(items.length * 52, 208)}
            mb={20}
            offsetScrollbars
          >
            <Stack spacing={6}>
              {items.length === 0 ? (
                <Text size="sm" color="dimmed" ta="center" py={12}>
                  Nenhum item adicionado ainda
                </Text>
              ) : (
                items.map((item) => (
                  <Flex
                    key={item._key}
                    align="center"
                    justify="space-between"
                    px={12}
                    py={8}
                    style={{
                      background: '#fff',
                      border: '1px solid #e9ecef',
                      borderRadius: 6,
                      borderLeft: `3px solid ${
                        item.situation === 'ok' ? '#2f9e44' : '#e67700'
                      }`,
                    }}
                  >
                    <Group spacing={8}>
                      <Text size="sm" fw={500}>
                        {employeeLabel[String(item.employeeId)] ?? '—'}
                      </Text>
                      <Text size="sm" color="dimmed">
                        ·
                      </Text>
                      <Text size="sm">
                        {epiLabel[String(item.epiId)] ?? '—'}
                      </Text>
                      <Badge
                        size="xs"
                        color={SITUATION_COLORS[item.situation] ?? 'gray'}
                      >
                        {SITUATION_LABEL[item.situation] ?? item.situation}
                      </Badge>
                      {item.observation && (
                        <Text size="xs" color="dimmed">
                          {item.observation}
                        </Text>
                      )}
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      size="sm"
                      onClick={() => removeItem(item._key)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Flex>
                ))
              )}
            </Stack>
          </ScrollArea>

          <Group position="right" mt={4}>
            <Button variant="outline" onClick={closeModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={editingId ? updateLoading : createLoading}
              disabled={!form.values.date}
            >
              {editingId ? 'Atualizar Ronda' : 'Salvar Ronda'}
            </Button>
          </Group>
        </form>
      </Modal>
    </Page>
  );
}
